/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { HiOutlineBars3 } from 'react-icons/hi2'
import { MdDashboard } from 'react-icons/md'
import { TbTruck, TbBuilding, TbMapPin, TbFileText, TbBell, TbSettings, TbExternalLink } from 'react-icons/tb'
import { CompanyDropdown } from '@cooltrax/jellyfish-sdk'
import { ModeSwitcher } from '@cooltrax/jellyfish-ui'

interface FreshTopBarMobileProps {
  hasUiDev: boolean
  hasInsights: boolean
  hasAuditAccess: boolean
  isAuthenticated: boolean
  redirectToLegacyFresh?: () => void
  hasLegacyUrl?: boolean
}

const JellyfishTopBarMobile: React.FC<FreshTopBarMobileProps> = ({
  hasUiDev,
  hasInsights,
  hasAuditAccess,
  isAuthenticated,
  redirectToLegacyFresh,
  hasLegacyUrl,
}) => {
  const location = useLocation()

  const isActive = (path: string) => {
    return location.pathname === path
      ? 'bg-base-300 text-primary font-semibold shadow-sm'
      : 'text-base-content hover:text-primary'
  }

  return (
    <div className="lg:hidden flex">
      <div className="flex items-center justify-end w-full gap-1.5">
        {isAuthenticated && (
          <div>
            <CompanyDropdown size="sm" variant="outline" />
          </div>
        )}
        <div className="dropdown dropdown-end">
          <div tabIndex={0} role="button" className="btn btn-ghost lg:hidden p-1" aria-label="Open menu">
            <HiOutlineBars3 className="h-6 w-6" />
          </div>
          {/* Mobile menu dropdown content */}
          <ul
            tabIndex={0}
            className="menu menu-sm dropdown-content mt-3 z-[1] p-3 shadow-2xl bg-base-100 rounded-box w-64 border border-base-300"
          >
            {hasInsights && (
              <li>
                <Link
                  to="/insights/views/default"
                  className={`${isActive('/insights')} flex items-center gap-2 hover:bg-base-200 p-2 text-sm xl:text-xs`}
                >
                  <MdDashboard className="text-accent h-4 w-4" /> Insights
                </Link>
              </li>
            )}
            {/* Main navigation items for ui:dev users */}
            {hasUiDev && (
              <>
                <li>
                  <Link
                    to="/fresh/in-transit"
                    className={`${isActive('/fresh/in-transit')} flex items-center gap-2 hover:bg-base-200 p-2 text-sm xl:text-xs`}
                  >
                    <TbTruck className="text-accent h-4 w-4" /> In Transit
                  </Link>
                </li>
                <li>
                  <Link
                    to="/fresh/in-store"
                    className={`${isActive('/fresh/in-store')} flex items-center gap-2 hover:bg-base-200 p-2 text-sm xl:text-xs`}
                  >
                    <TbBuilding className="text-accent h-4 w-4" /> In Store
                  </Link>
                </li>
                <li>
                  <Link
                    to="/fresh/geofences"
                    className={`${isActive('/fresh/geofences')} flex items-center gap-2 hover:bg-base-200 p-2 text-sm xl:text-xs`}
                  >
                    <TbMapPin className="text-accent h-4 w-4" /> Geofences
                  </Link>
                </li>
                <li>
                  <Link
                    to="/fresh/reports"
                    className={`${isActive('/fresh/reports')} flex items-center gap-2 hover:bg-base-200 p-2 text-sm xl:text-xs`}
                  >
                    <TbFileText className="text-accent h-4 w-4" /> Reports
                  </Link>
                </li>
                <li>
                  <Link
                    to="/fresh/alerts"
                    className={`${isActive('/fresh/alerts')} flex items-center gap-2 hover:bg-base-200 p-2 text-sm xl:text-xs`}
                  >
                    <TbBell className="text-accent h-4 w-4" /> Alerts
                  </Link>
                </li>
              </>
            )}

            {/* Admin link shown to users with ui:dev or audit access */}
            {(hasUiDev || hasAuditAccess) && (
              <>
                <li>
                  <Link
                    to="/fresh/admin"
                    className={`${isActive('/fresh/admin')} flex items-center gap-2 hover:bg-base-200 p-2 text-sm xl:text-xs`}
                  >
                    <TbSettings className="text-accent h-4 w-4" /> Admin
                  </Link>
                </li>
                <div className="divider my-1"></div>
              </>
            )}

            {/* Fresh (Legacy) link - shown to authenticated users */}
            {isAuthenticated && hasLegacyUrl && redirectToLegacyFresh && (
              <>
                <li>
                  <button
                    onClick={redirectToLegacyFresh}
                    className="flex items-center gap-2 hover:bg-base-200 p-2 text-sm xl:text-xs w-full text-left"
                  >
                    <TbExternalLink className="text-accent h-4 w-4" /> Fresh
                  </button>
                </li>
                <div className="divider my-1"></div>
              </>
            )}

            <li className="px-1">
              <ModeSwitcher />
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default JellyfishTopBarMobile
